import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cnabService, invoicesService, importLogService, sicrediService } from '../services/api';
import PeriodSelector, { periodToParams } from './PeriodSelector';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import { BoletoItem, boletoCapabilities, fraseDaBaixaParcial } from './boletos/boletoCapabilities';
import { useConfirm } from './shared/ConfirmDialog';
import { useBoletos } from './boletos/useBoletos';
import { useBoletoActions, tomDaMensagem } from './boletos/useBoletoActions';
import BoletoStatsCards, { Divergencias } from './boletos/BoletoStatsCards';
import BoletosTable from './boletos/BoletosTable';
import { RetornoHistoryPanel, RetornoResultPanel } from './boletos/RetornoPanels';
import { mensagemDoErroDeCarga } from '../design';
import { fmtDateTime } from '../utils/formatters';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import {
  ModalBaixaApi,
  ModalBaixaLote,
  ModalConciliacao,
  ModalEmitirHibrido,
  ModalLote,
  ModalVencimento,
} from './boletos/BoletoModals';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Um modal por vez — antes eram cinco estados independentes que podiam abrir juntos. */
type ModalState =
  | { kind: 'hibrido'; target: BoletoItem }
  | { kind: 'baixaApi'; target: BoletoItem }
  | { kind: 'baixaLote'; alvos: BoletoItem[] }
  | { kind: 'vencimento'; target: BoletoItem; value: string }
  | { kind: 'lote'; month: string; preview: any | null; running: boolean; job: any | null }
  | { kind: 'conciliacao'; dia: string; result: any | null }
  | null;

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * `canEdit`: aqui o backend NÃO é uniforme, e por isso não dá para esconder a
 * aba inteira. Em 0a0ff7d o cnab tem 4 rotas ADMIN+EDITOR e 7 só ADMIN:
 *
 *   editor PODE  → processRetorno (upload do arquivo de retorno), remessa
 *   só ADMIN     → emitirHibrido(+lote), baixaApi(+lote), conciliar (sicredi),
 *                  validateBoletos e invoices.update (alterar vencimento)
 *
 * O upload de retorno fica visível para editor de propósito: esconder o que ele
 * pode fazer é tão errado quanto oferecer o que ele não pode.
 */
const BoletosTab: React.FC<{ canEdit?: boolean }> = ({ canEdit = true }) => {
  const b = useBoletos();

  const [divergencias, setDivergencias] = useState<Divergencias | null>(null);

  // O card "Atenção" varre TODA a base (não a lista paginada), então vem de um
  // endpoint próprio. Recarregar só a lista deixava o card preso no número
  // velho até um F5 — dar baixa num título vivo não fazia o contador cair.
  const [divergenciasErro, setDivergenciasErro] = useState<string | null>(null);
  const loadDivergencias = useCallback(() => {
    sicrediService.getDivergencias()
      .then((d: any) => { setDivergencias(d); setDivergenciasErro(null); })
      .catch((e: any) => setDivergenciasErro(mensagemDoErroDeCarga(e)));
  }, []);

  const { reload } = b;
  const reloadAll = useCallback(async () => {
    await reload();
    loadDivergencias();
  }, [reload, loadDivergencias]);

  const { busy, isBusy, message, setMessage, runAction } = useBoletoActions(reloadAll);
  const confirmar = useConfirm();

  const [modal, setModal] = useState<ModalState>(null);
  const [retornoResult, setRetornoResult] = useState<any>(null);
  const [lastRetorno, setLastRetorno] = useState<any>(null);
  // C4 (12/10/2026): a leitura do último retorno que falha não vira "—" de quem nunca teve retorno.
  const [lastRetornoErro, setLastRetornoErro] = useState<string | null>(null);
  const lerUltimoRetorno = useCallback(() => {
    importLogService.getLast('CNAB_RETORNO')
      .then((r: any) => { setLastRetorno(r); setLastRetornoErro(null); })
      .catch((e: any) => setLastRetornoErro(mensagemDoErroDeCarga(e)));
  }, []);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [expandedBoletoHistId, setExpandedBoletoHistId] = useState<number | null>(null);
  const [historyPanel, setHistoryPanel] = useState<{ open: boolean; loading: boolean; rows: any[]; expandedId: number | null; erro?: string | null }>({
    open: false, loading: false, rows: [], expandedId: null, erro: null,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    lerUltimoRetorno();
    // Saúde da integração: se o webhook do Sicredi parar, o silêncio precisa
    // aparecer na tela — é o sintoma de fatura que nunca vira Paga sozinha.
    loadDivergencias();
  }, [loadDivergencias, lerUltimoRetorno]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  // ── Ações ────────────────────────────────────────────────────────────────
  const handleDownloadBoleto = async (item: BoletoItem) => {
    try {
      const blob = await invoicesService.getBoletoPdf(item.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Boleto_${item.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      // Título híbrido deve trazer o boleto OFICIAL do banco, que é o mesmo que
      // o cliente recebe. Cair no interno significa que o Sicredi não devolveu a
      // 2ª via, e o arquivo baixado difere do que foi enviado ao cliente.
      const origem = (blob as any).origem;
      if (item.hibrido && origem === 'interno') {
        setMessage({
          type: 'error',
          text: `Boleto ${item.code}: o Sicredi não devolveu a 2ª via oficial, baixamos o layout interno. Tente de novo em alguns minutos.`,
        });
      } else if (origem === 'oficial') {
        setMessage({ type: 'success', text: `Boleto oficial do Sicredi baixado (${item.code}).` });
      }
    } catch (err) {
      console.error('Erro ao baixar boleto:', err);
      setMessage({ type: 'error', text: 'Erro ao baixar boleto PDF' });
    }
  };

  const handleValidate = () =>
    runAction('validar', () => {
      const ids = b.boletos.filter(x => x.nossoNumero && x.status !== 'Cancelada').map(x => x.id);
      if (ids.length === 0) throw Object.assign(new Error(''), { response: { data: { message: 'Nenhum boleto para validar' } } });
      return invoicesService.validateBoletos(ids);
    }, {
      reloadAfter: false,
      onSuccess: (r: any) => {
        setValidationResult(r);
        setMessage(
          r.invalid === 0
            ? { type: 'success', text: `${r.valid} boleto(s) validado(s) — todos os códigos de barras estão corretos` }
            : { type: 'error', text: `${r.invalid} boleto(s) com erro no código de barras de ${r.total} verificados` },
        );
      },
    });

  // Baixa em lote PELA API: efeito imediato no banco, sem arquivo para subir no
  // portal e sem esperar o retorno da madrugada.
  //
  // E2 (12/10/2026, aprovado pelo Heitor): a falha PARCIAL era uma mensagem de sucesso que sumia em 5 s, com a seleção
  // limpa. Agora é warning fixa, nomeia cada título que falhou (código e nosso número, que o backend não devolve na
  // falha: saem da lista pelo invoiceId) e a seleção fica só com eles, prontos para tentar de novo.
  const handleBaixaLote = () =>
    runAction('baixaLote', () => cnabService.baixaApiLote(b.selection.paraBaixa), {
      irreversivel: true,
      success: (r: any) => (r.falhas?.length ? fraseDaBaixaParcial(r, b.boletos) : `${r.baixados.length} título(s) baixado(s) via API Sicredi — efeito imediato no banco.`),
      onSuccess: (r: any) => {
        if (r.falhas?.length) b.manterSo(r.falhas.map((f: any) => f.invoiceId));
        else b.clearSelection();
        setModal(null);
      },
    });

  // E5 (12/10/2026, aprovado pelo Heitor): soltar um arquivo na zona (ou escolher) processava na hora. O retorno
  // liquida faturas e baixa títulos; agora pergunta antes, dizendo o NOME do arquivo que vai entrar.
  const processRetornoFile = async (file: File | undefined) => {
    if (!file) return;
    const ok = await confirmar({
      title: `Processar o retorno ${file.name}?`,
      message: 'O retorno registra no HUB os pagamentos, baixas e recusas que o Sicredi devolveu. Confira se é o arquivo certo.',
      confirmLabel: 'Processar retorno',
    });
    if (!ok) return;
    setRetornoResult(null);
    return runAction('retorno', () => cnabService.processRetorno(file), {
      irreversivel: true,
      success: (r: any) => r.message || 'Retorno processado com sucesso.',
      onSuccess: async (r: any) => {
        setRetornoResult(r);
        lerUltimoRetorno();
        if (historyPanel.open) void loadHistory();
      },
    });
  };

  const loadHistory = useCallback(async () => {
    setHistoryPanel(h => ({ ...h, open: true, loading: true, erro: null }));
    try {
      const data = await importLogService.getAll({ importType: 'CNAB_RETORNO', limit: 50 });
      setHistoryPanel(h => ({ ...h, loading: false, erro: null, rows: Array.isArray(data) ? data : data.data || [] }));
    } catch (e) {
      // C5 (12/10/2026): a falha dizia "Nenhum retorno processado". Agora diz que não leu.
      setHistoryPanel(h => ({ ...h, loading: false, erro: mensagemDoErroDeCarga(e) }));
    }
  }, []);

  const handleEmitirHibrido = (target: BoletoItem) =>
    runAction('hibrido', () => cnabService.emitirHibrido(target.id), {
      irreversivel: true,
      success: (r: any) => `Boleto híbrido registrado (fatura ${r.code}, nosso número ${r.nossoNumero}) — QR PIX pronto no e-mail e no PDF.`,
      onSuccess: () => setModal(null),
    });

  const handleBaixaApi = (target: BoletoItem) =>
    runAction('baixaApi', () => cnabService.baixaApi(target.id), {
      irreversivel: true,
      success: (r: any) => `Boleto ${r.nossoNumero} (fatura ${r.code}) baixado via API Sicredi — efeito imediato no banco.`,
      onSuccess: () => setModal(null),
    });

  const handleSaveDueDate = (target: BoletoItem, value: string) =>
    runAction('vencimento', () => invoicesService.update(target.id, { dueDate: value }), {
      success: () => `Vencimento da fatura ${target.code} alterado com sucesso.`,
      onSuccess: () => setModal(null),
    });

  // ── Lote híbrido ─────────────────────────────────────────────────────────
  const loadLotePreview = async (month: string) => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    try {
      const preview = await cnabService.emitirHibridoLote(month, true);
      setModal(m => (m?.kind === 'lote' && m.month === month ? { ...m, preview } : m));
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Erro ao listar candidatas do lote.' });
    }
  };

  const openLote = () => {
    // O mês NUNCA é inferido: emitir registra título de verdade no banco.
    // periodToParams devolve {} em "Todos os Meses"/período e "2026-05,2026-06"
    // em multi-mês, então só pré-preenche quando há exatamente um mês escolhido.
    const p = periodToParams(b.periodFilter);
    const mesUnico = p.month && !p.month.includes(',') ? p.month : '';
    setModal({ kind: 'lote', month: mesUnico, preview: null, running: false, job: null });
    if (mesUnico) void loadLotePreview(mesUnico);
  };

  // O backend devolve o jobId na hora e emite em background (o nginx corta
  // requisição em 60s e o lote leva minutos). A tela acompanha por polling.
  const pollLoteJob = useCallback((jobId: number) => {
    const tick = async () => {
      try {
        const job = await cnabService.getLoteJob(jobId);
        setModal(m => (m?.kind === 'lote' ? { ...m, job, running: job.status === 'running' } : m));
        if (job.status === 'running') {
          setTimeout(tick, 2000);
          return;
        }
        void reloadAll();
      } catch {
        setMessage({ type: 'error', text: 'Perdi o acompanhamento do lote. Ele continua rodando no servidor.' });
        setModal(m => (m?.kind === 'lote' ? { ...m, running: false } : m));
      }
    };
    void tick();
  }, [reloadAll, setMessage]);

  const handleRunLote = async () => {
    if (modal?.kind !== 'lote' || !/^\d{4}-\d{2}$/.test(modal.month)) return;
    setModal({ ...modal, running: true });
    try {
      const { jobId } = await cnabService.emitirHibridoLote(modal.month, false);
      pollLoteJob(jobId);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Erro na emissão em lote.' });
      setModal(m => (m?.kind === 'lote' ? { ...m, running: false } : m));
    }
  };

  const handleConciliar = (dryRun: boolean) => {
    if (modal?.kind !== 'conciliacao') return;
    const [y, m, d] = modal.dia.split('-');
    const diaBR = `${d}/${m}/${y}`;
    void runAction('conciliar', () => sicrediService.conciliar(diaBR, dryRun), {
      reloadAfter: !dryRun,
      onSuccess: (r: any) => {
        setModal(prev => (prev?.kind === 'conciliacao' ? { ...prev, result: r } : prev));
        if (!dryRun && r?.aplicados > 0) {
          setMessage({ type: 'success', text: `${r.aplicados} liquidação(ões) aplicada(s) a partir do extrato do Sicredi.` });
        }
      },
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────
  // O menu de operações no padrão da Central (27/10/2026): caixa no cartão, item neutro, hover no elevado.
  const menuItem = 'w-full text-left px-3 py-2 text-sm text-central-texto hover:bg-central-elevado flex items-center gap-2';

  return (
    <div className="space-y-6">
      {message && (
        // K6 (12/10/2026): a faixa de mensagem usa o mapa de tons, não verde e vermelho próprios.
        <div className={`p-4 rounded-lg border ${TONE_SURFACE[tomDaMensagem(message)]} ${TONE_BORDER[tomDaMensagem(message)]} ${TONE_TEXT[tomDaMensagem(message)]}`}>
          <div className="flex items-center justify-between">
            <span role={message.fixa ? 'alert' : 'status'} className="whitespace-pre-line">{message.text}</span>
            <button onClick={() => { setMessage(null); setValidationResult(null); }} className="text-xs opacity-60 hover:opacity-100 ml-4">Fechar</button>
          </div>
          {validationResult && validationResult.invalid > 0 && (
            <div className="mt-3 space-y-1">
              {validationResult.errors.map((err: any, i: number) => (
                <div key={i} className="text-sm">{err.code} - {err.clientName}: {err.error}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Barra de ações: uma primária + um menu. As ações de massa moram na
          barra de seleção, então não existe mais botão desabilitado esperando
          que o operador adivinhe o que selecionar. */}
      <ListToolbar
        period={<PeriodSelector value={b.periodFilter} onChange={b.setPeriodFilter} onRefresh={reloadAll} />}
        search={<ToolbarSearch value={b.searchFilter} onChange={b.setSearchFilter} placeholder="Buscar cliente, CNPJ, fatura..." />}
        actions={<>
          {canEdit && <button
            onClick={openLote}
            className="px-4 py-2 bg-primary hover:bg-primary-strong rounded-lg text-white font-medium transition-colors flex items-center gap-2"
            title="Registra boleto híbrido (QR PIX) via API pra todas as faturas de boleto do mês sem boleto ativo — não envia e-mail"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Emitir boletos do mês
          </button>}

          {/* Menu de operações ocasionais — o CNAB continua acessível, só deixou
              de competir com a emissão pelo espaço nobre da barra. */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors"
              title="Mais ações: validar, conciliar, retorno CNAB"
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-purple-400'); }}
              onDragLeave={e => e.currentTarget.classList.remove('ring-2', 'ring-purple-400')}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.classList.remove('ring-2', 'ring-purple-400');
                if (busy !== 'retorno' && e.dataTransfer?.files?.length) void processRetornoFile(e.dataTransfer.files[0]);
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" />
              </svg>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 z-40 w-64 bg-central-cartao border border-central-borda rounded-lg shadow-xl py-1 overflow-hidden" data-menu-de-operacoes>
                {/* Validar (invoices/validate-boletos) e Conciliar (sicredi) são
                    ADMIN no backend; o retorno CNAB logo abaixo é ADMIN+EDITOR. */}
                {canEdit && <button
                  onClick={() => { setMenuOpen(false); void handleValidate(); }}
                  disabled={isBusy('validar') || b.boletos.length === 0}
                  className={`${menuItem} disabled:opacity-40`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  {isBusy('validar') ? 'Validando…' : 'Validar códigos de barras'}
                </button>}
                {canEdit && <button
                  onClick={() => { setMenuOpen(false); setModal({ kind: 'conciliacao', dia: hojeISO(), result: null }); }}
                  className={menuItem}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Conciliar com o Sicredi
                </button>}
                <div className="my-1 border-t border-central-borda" />
                <p className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wide text-slate-500">CNAB 240 (fallback)</p>
                <label
                  className={`${menuItem} cursor-pointer`}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('bg-slate-700'); }}
                  onDragLeave={e => e.currentTarget.classList.remove('bg-slate-700')}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-slate-700');
                    if (busy !== 'retorno' && e.dataTransfer?.files?.length) { setMenuOpen(false); void processRetornoFile(e.dataTransfer.files[0]); }
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                  {isBusy('retorno') ? 'Processando…' : 'Enviar arquivo de retorno'}
                  <input
                    type="file"
                    accept=".ret,.RET,.crt,.CRT,.txt"
                    className="hidden"
                    disabled={isBusy('retorno')}
                    onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; setMenuOpen(false); void processRetornoFile(f); }}
                  />
                </label>
                <button
                  onClick={() => { setMenuOpen(false); historyPanel.open ? setHistoryPanel(h => ({ ...h, open: false })) : void loadHistory(); }}
                  className={menuItem}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Histórico de retornos
                </button>
              </div>
            )}
          </div>
        </>}
      />

      <BoletoStatsCards
        stats={b.stats}
        cardFilter={b.cardFilter}
        onFilter={b.setCardFilter}
        divergencias={divergencias}
        divergenciasErro={divergenciasErro}
        onFocusAtencao={() => {
          // As divergências vivem fora do mês corrente, então focar nelas exige
          // largar o filtro de período — senão o clique não mostraria nada.
          b.setPeriodFilter({ mode: 'month', months: [], startDate: '', endDate: '' });
          b.setCardFilter('atencao');
        }}
        lastRetorno={lastRetorno}
        lastRetornoErro={lastRetornoErro}
        lido={b.lido}
      />

      {/* Barra de seleção: as ações de massa vivem aqui, aparecendo só quando
          existe alvo e com a contagem exata de cada uma. */}
      {b.desmarcados > 0 && b.selection.count === 0 && (
        <p role="status" className="text-xs text-slate-400" data-desmarcados>
          {b.desmarcados === 1 ? '1 boleto desmarcado' : `${b.desmarcados} boletos desmarcados`} ao trocar o cartão ou o período
        </p>
      )}
      {b.selection.count > 0 && (
        // K4 (12/10/2026): a barra de seleção marca por peso, não pelo azul.
        <div className="bg-slate-800/50 border border-slate-500 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-white font-semibold">{b.selection.count} boleto(s) selecionado(s)</span>
            <span className="text-white font-mono font-medium">R$ {fmt(b.selection.total)}</span>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && b.selection.paraBaixa.length > 0 && (
              <button
                onClick={() => setModal({
                  kind: 'baixaLote',
                  alvos: b.boletos.filter(x => b.selection.paraBaixa.includes(x.id)),
                })}
                disabled={isBusy('baixaLote')}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-lg text-white text-sm transition-colors disabled:opacity-50"
              >
                {isBusy('baixaLote') ? 'Baixando…' : `Baixar (${b.selection.paraBaixa.length})`}
              </button>
            )}
            <button onClick={b.clearSelection} className="text-slate-400 hover:text-slate-300 text-sm px-2">Limpar seleção</button>
          </div>
        </div>
      )}

      {historyPanel.open && (
        <RetornoHistoryPanel
          loading={historyPanel.loading}
          erro={historyPanel.erro}
          onTentarDeNovo={loadHistory}
          history={historyPanel.rows}
          expandedId={historyPanel.expandedId}
          onToggle={id => setHistoryPanel(h => ({ ...h, expandedId: h.expandedId === id ? null : id }))}
          onClose={() => setHistoryPanel(h => ({ ...h, open: false }))}
        />
      )}

      {retornoResult && <RetornoResultPanel result={retornoResult} onClose={() => setRetornoResult(null)} />}

      {modal?.kind === 'lote' && (
        <ModalLote
          state={modal}
          onChangeMonth={month => { setModal({ ...modal, month, preview: null }); if (month) void loadLotePreview(month); }}
          onClose={() => setModal(null)}
          onRun={handleRunLote}
        />
      )}
      {modal?.kind === 'hibrido' && (
        <ModalEmitirHibrido
          target={modal.target}
          running={isBusy('hibrido')}
          onCancel={() => setModal(null)}
          onConfirm={() => void handleEmitirHibrido(modal.target)}
        />
      )}
      {modal?.kind === 'baixaApi' && (
        <ModalBaixaApi
          target={modal.target}
          running={isBusy('baixaApi')}
          onCancel={() => setModal(null)}
          onConfirm={() => void handleBaixaApi(modal.target)}
        />
      )}
      {modal?.kind === 'baixaLote' && (
        <ModalBaixaLote
          alvos={modal.alvos}
          running={isBusy('baixaLote')}
          onCancel={() => setModal(null)}
          onConfirm={() => void handleBaixaLote()}
        />
      )}
      {modal?.kind === 'vencimento' && (
        <ModalVencimento
          target={modal.target}
          value={modal.value}
          saving={isBusy('vencimento')}
          onChange={value => setModal({ ...modal, value })}
          onCancel={() => setModal(null)}
          onSave={() => void handleSaveDueDate(modal.target, modal.value)}
          onBaixarPrimeiro={canEdit && boletoCapabilities(modal.target).canBaixarApi ? () => setModal({ kind: 'baixaApi', target: modal.target }) : undefined}
        />
      )}
      {modal?.kind === 'conciliacao' && (
        <ModalConciliacao
          dia={modal.dia}
          running={isBusy('conciliar')}
          result={modal.result}
          onChangeDia={dia => setModal({ ...modal, dia, result: null })}
          onClose={() => setModal(null)}
          onRun={handleConciliar}
        />
      )}

      {/* C1 (12/10/2026): a recarga que falha não apaga a lista lida; a faixa diz que não atualizou e de quando é. */}
      {b.loadError && b.lido && (
        <div role='alert' className={`px-4 py-3 rounded-lg border text-sm flex items-center justify-between gap-3 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-boletos-desatualizados>
          <span>Não consegui atualizar os boletos ({b.loadError}). Mostrando os lidos em {b.lidoEm ? fmtDateTime(b.lidoEm) : '—'}.</span>
          <button type='button' onClick={reloadAll} className='underline whitespace-nowrap'>Tentar de novo</button>
        </div>
      )}

      <BoletosTable
        boletos={b.filtered}
        loading={b.loading}
        loadError={b.loadError}
        searchFilter={b.searchFilter}
        sort={b.sort}
        toggleSort={b.toggleSort}
        selected={b.selected}
        selectableCount={b.selectable.length}
        onToggleSelect={b.toggleSelect}
        onToggleAll={b.toggleAll}
        expandedHistId={expandedBoletoHistId}
        onToggleHist={id => setExpandedBoletoHistId(prev => (prev === id ? null : id))}
        onReload={reloadAll}
        onEditDueDate={item => setModal({ kind: 'vencimento', target: item, value: item.dueDate?.split('T')[0] || '' })}
        onEmitirHibrido={item => setModal({ kind: 'hibrido', target: item })}
        onBaixarApi={item => setModal({ kind: 'baixaApi', target: item })}
        onDownloadPdf={handleDownloadBoleto}
      />
    </div>
  );
};

export default BoletosTab;
